import type {
  AmbulanceReportMediaListResponse,
  AmbulanceReportMediaUploadResponse,
  LocationRequestBody,
  LocationRequestResponse,
  ReportCompleteResponse,
  ReportDeleteResponse,
  ReportDetailsResponse,
  ReportMediaListResponse,
  ReportOfficerListResponse,
  ReportUpdateBody,
  ReportUpdateResponse,
} from '@/domains/traffic-monitoring/types/report-details.types';
import { request } from '@/shared/utils/request';

export const reportDetailsApi = {
  // GET /api/dsh/report/{report_id} -> { report, patients[], ambulance_reports[], counts }
  getReportDetails: async (reportId: number): Promise<ReportDetailsResponse> => {
    return request.get<ReportDetailsResponse>(`/report/${reportId}`);
  },

  // PUT /api/dsh/report/{report_id} -> { report_id, updated_time, ... }
  // Editable for the report's whole life, including after completion/rejection
  // (since 2026-09-03). The conflict/reason refusal is legacy and no longer
  // emitted; callers may keep handling it defensively for older backends.
  putReportDetails: async (
    reportId: number,
    body: ReportUpdateBody
  ): Promise<ReportUpdateResponse> => {
    return request.put<ReportUpdateResponse>(`/report/${reportId}`, body);
  },

  // DELETE /api/dsh/report/{report_id} -> { report_id, deleted, deleted_time } | { conflict, reason }
  // Soft delete. Refused with conflict: true while any ambulance is still
  // working the report - the crew app resolves its dispatch through it.
  deleteReport: async (reportId: number): Promise<ReportDeleteResponse> => {
    return request.delete<ReportDeleteResponse>(`/report/${reportId}`);
  },

  // GET /api/dsh/report/officers -> { total_count, officer_list[] }
  // The PSC operators of the caller's organization - the only users a report's
  // ambulance reception officer may be.
  getReportOfficers: async (): Promise<ReportOfficerListResponse> => {
    return request.get<ReportOfficerListResponse>('/report/officers');
  },

  // POST /api/dsh/report/{report_id}/complete -> { report_id, decided_time, replayed, conflict? }
  postReportComplete: async (reportId: number): Promise<ReportCompleteResponse> => {
    return request.post<ReportCompleteResponse>(`/report/${reportId}/complete`);
  },

  // GET /api/dsh/report/{report_id}/media -> { report_id, total_count, media_list[] }
  getReportMedia: async (reportId: number): Promise<ReportMediaListResponse> => {
    return request.get<ReportMediaListResponse>(`/report/${reportId}/media`);
  },

  // POST /api/dsh/report/{report_id}/location-request -> { link_url, expires_at, phone_number_masked, ... }
  postLocationRequest: async (
    reportId: number,
    body: LocationRequestBody = {}
  ): Promise<LocationRequestResponse> => {
    return request.post<LocationRequestResponse>(`/report/${reportId}/location-request`, body);
  },

  // GET /api/dsh/ambulance-report/{ambulance_report_id}/media -> { ambulance_report_id, total_count, media_list[] }
  getAmbulanceReportMedia: async (
    ambulanceReportId: number
  ): Promise<AmbulanceReportMediaListResponse> => {
    return request.get<AmbulanceReportMediaListResponse>(
      `/ambulance-report/${ambulanceReportId}/media`
    );
  },

  // POST /api/dsh/ambulance-report/{ambulance_report_id}/media -> { ambulance_report_id, uploaded[] }
  // 1..5 JPEG/PNG files, 10MB each - validated server-side by magic bytes, not
  // by client filename/mime, so no accept-attribute check is duplicated here.
  postAmbulanceReportMedia: async (
    ambulanceReportId: number,
    files: File[]
  ): Promise<AmbulanceReportMediaUploadResponse> => {
    const formData = new FormData();
    for (const file of files) formData.append('files', file);
    return request.post<AmbulanceReportMediaUploadResponse>(
      `/ambulance-report/${ambulanceReportId}/media`,
      formData
    );
  },
};
